import { CurrentUser, SubuserCreateRequestDto, UserCreateRequestDto } from 'src/app/core/models/user';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { User } from 'src/app/core/models/user';
import { ApiService } from './api.service';
import { Account, AccountSettingsForm, UserUpdateForm } from 'src/app/core/models/account';
import { LocalStorageService } from '../utils/local-storage.service';
import { CurrentUserForm } from 'src/app/core/models/dtos';
import { HttpClient } from '@angular/common/http';
import { LogoGetDto, LogoMetadataDto } from '../../models/logo-dtos';
import { AccountRepository } from '@/core/repositories/account/account.repository';
import { logError } from '@/core/utils/rxjs/log-error.operator';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { DataUtils } from '@/core/utils/data-utils';
import { sha256 } from 'js-sha256';
import { CURRENT_USER_CACHE_TTL, ACCOUNT_CACHE_TTL } from '@/core/config/cache-ttl';
import { UserState } from '@/core/models/user-state';

@Injectable({ providedIn: 'root' })
export class UserService extends ApiService {
    private isLoggingOut = false;
    // current user cache store:
    private readonly currentUserStore = new TtlCacheStore<CurrentUser>(
        'currentUser',
        new CurrentUser(),
        CURRENT_USER_CACHE_TTL,
        () =>
            this.accountRepository.getCurrentUser().pipe(
                tap((user) => {
                    this.storeProfile(user);
                }),
            ),
    );
    public currentUser$ = this.currentUserStore.state$;
    // current users account cache store:
    private readonly accountStore = new TtlCacheStore<Account>('account', new Account(), ACCOUNT_CACHE_TTL, () =>
        this.accountRepository.getAccount(),
    );
    // cached account observable:
    public account$ = this.accountStore.state$;
    // observable for receiving account setting changes:
    private accountSettingsChanged = new Subject<void>();
    accountSettingsChanged$ = this.accountSettingsChanged.asObservable();

    /**
     * Unsafe method for getting the current user cached value. It returns the current user.
     * Attention: This method may return undefined if the user is not loaded yet.
     * @deprecated Use currentUser$ or getCurrentUser() instead.
     * @returns CurrentUser | undefined - The current user or undefined if not loaded yet.
     */
    public get currentUser(): CurrentUser | undefined {
        if (!this.currentUserStore.ready()) {
            console.error('UserService.currentUser - Accessing currentUser before it is loaded!');
            return undefined;
        }
        return this.currentUserStore.get();
    }

    constructor(
        private readonly accountRepository: AccountRepository,
        http: HttpClient,
    ) {
        super(http);
    }

    public login(loginName: string, password: string, keepLogin: boolean): Observable<string> {
        return this._http
            .post(ApiService.openApiUrl + 'account/login', {
                username: loginName,
                password: password,
                keepLogin: keepLogin,
            })
            .pipe(
                map((response) => {
                    return response.toString();
                }),
            );
    }

    public ssoLogin(ssoToken: string): Observable<string> {
        return this._http
            .post<string>(ApiService.privateApiUrl + 'account/ssologin', {
                token: ssoToken,
            })
            .pipe(
                map((response: string) => {
                    return response.toString();
                }),
            );
    }

    public initiateLogout(): void {
        this.isLoggingOut = true;
    }

    public logoutFinished(): void {
        this.isLoggingOut = false;
    }

    private storeProfile(curUser: CurrentUser): void {
        // Note this originally stored the http response JSON object,
        // but in case of update already the old currentUser object...
        // Now it always stores the CurrentUser object (after mapping).
        // So no matter if it was loaded or updated, the local storage always contains a user object of the same structure.
        // The only use case at the moment is in the auth service to get the templateId
        try {
            LocalStorageService.storeProfile(curUser);
        } catch (error) {
            console.error('Error storing profile in local storage:', error);
        }
    }

    /**
     * Loads the current user profile. The method caches the current user.
     * This method can be used to emit account$ changes to refresh the subscriptions.
     * @param forceLoad If true, the current user is always loaded from the server, even if it is already cached.
     * @returns Observable<CurrentUser> An observable that emits the current user.
     */
    public loadCurrentUser(forceLoad: boolean = false): Observable<CurrentUser> {
        if (this.isLoggingOut) {
            return this.currentUserStore.getCached(); // do not load / refresh while logout is being processed
        }
        return this.currentUserStore.load(forceLoad);
    }

    /**
     * Async alternative for getting the current user.
     * It delivers the same result as loadCurrentUserProfile() but returns a Promise.
     * Calling this method is in most cases fast as the current user object is cached.
     * @returns Observable<CurrentUser> - The current users
     */
    public getCurrentUser(): Promise<CurrentUser> {
        return this.currentUserStore.getAsync();
    }

    private updateCurrentUserCache(): void {
        this.loadCurrentUser(true).subscribe(); // TODO return observable and use switchMap instead of subscribe() here to avoid race condition
    }

    private updateAccountCache(): void {
        this.loadAccount(true).subscribe(); // TODO return observable and use switchMap instead of subscribe() here to avoid race condition
    }

    /**
     * Loads the current users account including account settings and account users.
     * The method caches the account.
     *
     * @param forceLoad If true, the account is always loaded from the server, even if it is already cached.
     * @returns Observable<Account> An observable that emits the current users account.
     */
    public loadAccount(forceLoad: boolean = false): Observable<Account> {
        if (this.isLoggingOut) {
            return this.accountStore.getCached(); // do not load / refresh while logout is being processed
        }
        return this.accountStore.load(forceLoad);
    }

    /**
     * Async alternativ for getting the current user's account.
     * It delivers the same result as loadAccount() but returns a Promise.
     * Calling this method is in most cases fast as the account object is cached.
     * @returns Promise<Account> - The current users account.
     */
    public async getAccount(): Promise<Account> {
        return await this.accountStore.getAsync();
    }

    /**
     * Clears all caches of the user service (account and current user).
     * Clears the caches and observables (e.g., on logout).
     */
    clearCache(): void {
        this.accountStore.reset();
        this.currentUserStore.reset();
    }

    createAccount(user: UserCreateRequestDto): Observable<User> {
        return this.accountRepository.createAccount(user);
    }

    activateUser(token: string): Observable<void> {
        return this.accountRepository.activateUser(token);
    }

    requestPasswordReset(mail: string): Observable<void> {
        return this.accountRepository.requestPasswordReset(mail);
    }

    sendNewPassword(token: string): Observable<void> {
        return this.accountRepository.sendNewPassword(token);
    }

    resendActivationCode(token: string): Observable<void> {
        return this.accountRepository.resendActivationCode(token);
    }

    deleteUser(userId: number | undefined): Observable<void> {
        return this.accountRepository.deleteUser(userId);
    }

    /** Sends only provided fields using the existing PUT endpoint and updates the same caches. */
    public patchCurrentUser(
        changes: UserUpdateForm,
        password?: string,
        isMailChange?: boolean,
    ): Observable<CurrentUser> {
        const currentUserForm = { ...changes };
        if (password) {
            currentUserForm.password = sha256(password);
        }
        return this.accountRepository.updateCurrentUserForm(currentUserForm).pipe(
            map((response: CurrentUserForm) => {
                const curUser = CurrentUser.fromJson(response);
                this.storeProfile(curUser);
                this.currentUserStore.set(curUser);
                // when the email address is changed, the login token becomes invalid; as a result, api requests will fail,
                // that is why the account cache cannot be updated after an email change instead the user needs to re-authenticate (logout -> login)
                if (!isMailChange) {
                    if (!this.patchAccount(curUser)) {
                        this.updateAccountCache();
                    }
                }
                return curUser;
            }),
        );
    }

    /**
     * Tries to update the user object in the account without reloading the whole account.
     * @param user The accounts user to be updated.
     * @returns true if the user was updated, false otherwise.
     */
    private patchAccount(user: User): boolean {
        if (!this.accountStore.ready()) {
            return false;
        }
        const account = this.accountStore.get();
        if (!account) {
            return false;
        }
        const userIndex = account.users.findIndex((u) => u.id === user.id);
        if (userIndex === -1) {
            return false;
        }
        account.users[userIndex] = User.toUserForm(user);
        this.accountStore.set(account);
        return true;
    }

    /**
     * Performs a partial update of the account settings.
     * Only provided fields are updated.
     * It updates the account observable and emits an event.
     *
     * @param settings A partial AccountSettingsForm. Any provided field will be updated.
     */
    patchAccountSettings(settings: Partial<AccountSettingsForm>): Observable<void> {
        return this.accountRepository.patchAccountSettings(settings).pipe(
            tap(() => {
                // programmatically patches the account object without reloading it via api request
                const account = this.accountStore.get();
                DataUtils.patchObject(account.accountSettings, settings);
                this.accountStore.set(account);
                this.accountSettingsChanged.next();
                this.updateCurrentUserCache();
            }),
        );
    }

    createSubuser(userCreateDto: SubuserCreateRequestDto): Observable<User> {
        return this.accountRepository.createSubuser(userCreateDto).pipe(
            tap(() => {
                this.updateAccountCache();
            }),
        );
    }

    patchSubuser(userId: number, changes: UserUpdateForm): Observable<User> {
        return this.accountRepository.patchSubuser(userId, changes).pipe(
            tap(() => {
                this.updateAccountCache();
            }),
        );
    }

    /** Activates or deactivates a subuser without touching any other field of the record. */
    updateSubuserState(userId: number, state: UserState): Observable<User> {
        return this.patchSubuser(userId, { state });
    }

    getLicensesURL(): Observable<string> {
        return this.accountRepository.getLicensesURL();
    }

    createAPIKey(): Observable<string> {
        return this.accountRepository.createAPIKey();
        // .pipe(switchMap((apiKey) => this.loadCurrentUserProfile(true).pipe(map(() => apiKey))));
    }

    deleteAPIKey(): Observable<void> {
        return this.accountRepository.deleteAPIKey();
        // .pipe(mergeMap(() => this.loadCurrentUserProfile()));
    }

    startTrial(date: string): Observable<void> {
        return this.accountRepository.startTrial(date).pipe(
            tap(() => {
                this.updateAccountCache();
                this.updateCurrentUserCache();
            }),
        );
    }

    /**
     * Fetches company logo metadata (version and lastModified).
     * Returns an object with null values if the logo does not exist.
     * @returns Observable<LogoMetadataDto> with "version" and "lastModified" fields (both nullable)
     */
    getReportLogoMetadata(): Observable<LogoMetadataDto> {
        return this.accountRepository.getReportLogoMetadata().pipe(logError('Error fetching logo metadata'));
    }

    /**
     * Uploads a company logo image (PNG or JPEG only).
     * @param file The logo image file to upload
     * @returns Observable<void> - completes on success (204 No Content)
     */
    uploadReportLogo(file: File): Observable<void> {
        return this.accountRepository.uploadReportLogo(file).pipe(logError('Error uploading logo'));
    }

    /**
     * Fetches the company logo as a base64 encoded image.
     * @returns Observable<LogoGetDto> with base64 string (null if no logo exists)
     */
    fetchReportLogo(): Observable<LogoGetDto> {
        return this.accountRepository.fetchReportLogo().pipe(logError('Error fetching logo'));
    }

    /**
     * Deletes the company logo from storage.
     * @returns Observable<void> - completes on success (204 No Content)
     */
    deleteReportLogo(): Observable<void> {
        return this.accountRepository.deleteReportLogo().pipe(logError('Error deleting logo'));
    }
}
